export type ProductCardImageKind = 'product' | 'product_gray' | 'celebrity' | 'material' | 'other';

export interface ProductCardImageInfo {
  src: string;
  kind: ProductCardImageKind;
}

interface ProductCardImageSource {
  image?: {
    celebrity?: string[];
    material?: string[];
    product?: string[];
    product_gray?: string[];
  };
}

interface ProductCardImages {
  productImage: ProductCardImageInfo;
  hoverProductImage: ProductCardImageInfo;
  shouldShowHoverProductImage: boolean;
}

const EMPTY_IMAGE: ProductCardImageInfo = {
  src: '',
  kind: 'other',
};

export function shouldCoverProductCardImage(kind: ProductCardImageKind): boolean {
  return kind === 'celebrity' || kind === 'material';
}

function getFirstImage(
  candidates: Array<[ProductCardImageKind, string[] | undefined]>,
): ProductCardImageInfo {
  const matchedImage = candidates.find(([, images]) => images?.[0]);

  if (!matchedImage) return EMPTY_IMAGE;

  const [kind, images] = matchedImage;

  return {
    src: images?.[0] || '',
    kind,
  };
}

function getFirstDistinctImage(
  kind: ProductCardImageKind,
  images: string[] | undefined,
  currentSrc: string,
): ProductCardImageInfo | null {
  const src = images?.find((candidate) => Boolean(candidate) && candidate !== currentSrc);

  return src ? { src, kind } : null;
}

function getHoverImage(
  product: ProductCardImageSource,
  productImage: ProductCardImageInfo,
): ProductCardImageInfo {
  const {
    celebrity,
    material,
    product: whiteProduct,
    product_gray: grayProduct,
  } = product.image || {};

  return (
    getFirstDistinctImage('celebrity', celebrity, productImage.src) ??
    getFirstDistinctImage('material', material, productImage.src) ??
    getFirstDistinctImage('product_gray', grayProduct, productImage.src) ??
    getFirstDistinctImage('product', whiteProduct, productImage.src) ??
    EMPTY_IMAGE
  );
}

export function getProductCardImages(product: ProductCardImageSource): ProductCardImages {
  const {
    celebrity,
    material,
    product: whiteProduct,
    product_gray: grayProduct,
  } = product.image || {};
  const productImage = getFirstImage([
    ['product_gray', grayProduct],
    ['product', whiteProduct],
    ['material', material],
    ['celebrity', celebrity],
  ]);
  const hoverProductImage = getHoverImage(product, productImage);

  return {
    productImage,
    hoverProductImage,
    shouldShowHoverProductImage: Boolean(hoverProductImage.src),
  };
}
